import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import numeral from 'numeral';

import dynamic from 'next/dynamic';
import Dotdotdot from 'react-dotdotdot';

import Button from '~/components/Form/Button';
import MessageAgentButton from '~/components/Agents/MessageAgent/MessageAgentButton';
import {
  renderAboutAgent,
  achievementBadgesExist,
  formatAgentProfileURL,
  achievementsExist,
} from '~/utils/AgentUtils';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  faBriefcase,
  faCaretRight,
  faCheckCircle,
  faEnvelope,
  faGraduationCap,
  faStar,
} from '@fortawesome/pro-solid-svg-icons';
import PrimeBadge from './Profile/Prime/PrimeBadge';
import RespondsIn from './Profile/RespondsIn';
import OpenAt from './Profile/OpenAt';
import HeaderStat from './Profile/Header/HeaderStat';
import ReviewRatings from './Reviews/ReviewRatings';
import VerifiedBadge from '../VerifiedBadge';
import Achievements from './Profile/Sections/Achievements';
import ProfileCompleteBadge from './Profile/ProfileCompleteBadge';

import { COLORS } from '../../config/themes';
import { capitalize } from '../../utils/TextProcessing';
import { useDisplayNameOrFirstName } from '../../utils/AgentUtils';

const Image = dynamic(() => import('~/components/Image'), { ssr: false });
const VideoPlayer = dynamic(import('../Common/VideoPlayer'), {
  ssr: false,
});

const TopPerformingAgentsCard = ({
  agent,
  isPrime,
  isNonPrime,
  location,
  inNetwork,
  messageActionTextFunc,
  skipLocation,
  noCompetingProposals,
  messageHeader,
}) => {
  const router = useRouter();

  const displayNameOrFirstName = useDisplayNameOrFirstName(agent);

  const renderSpecialties = () => {
    let specialties = agent.specialties.split('#');
    if (specialties.length > 4) {
      specialties = specialties.slice(0, 4);
    }
    return specialties.join(', ');
  };

  const renderDesignations = () => {
    let { designations } = agent;
    if (designations) {
      designations = designations.split('#');
      const filteredDesignations = [];
      for (let i = 0; i < designations.length; i++) {
        let designation = designations[i].replace('&reg;', '®');
        if (designations[i].toLowerCase().includes('realtor')) {
          designation = 'REALTOR®';
        }
        if (!filteredDesignations.includes(designation)) {
          filteredDesignations.push(designation);
        }
      }
      return filteredDesignations.join(', ');
    }
    return null;
  };

  const achievementsSectionExists = () =>
    achievementBadgesExist({ agent }) ||
    educationExists() ||
    achievementsExist({ achievements: agent.achievements }) ||
    (agent.agent_awards !== 0 &&
      agent.agent_awards !== null &&
      agent.agent_awards.trim() !== '') ||
    (agent.designations !== 0 &&
      agent.designations !== null &&
      agent.designations.trim() !== '');

  const educationExists = () => {
    let exists = false;
    if (agent.highest_education !== '' && agent.highest_education !== null) {
      exists = true;
    }
    if (agent.university_name !== '' && agent.university_name !== null) {
      exists = true;
    }
    if (agent.degree_name !== '' && agent.degree_name !== null) {
      exists = true;
    }

    return exists;
  };

  const renderEducation = () => {
    const lineOneArray = [];
    const lineTwoArray = [];
    if (agent.highest_education !== '' && agent.highest_education !== null) {
      lineOneArray.push(agent.highest_education);
    }
    if (agent.university_name !== '' && agent.university_name !== null) {
      lineOneArray.push(agent.university_name);
    }
    if (agent.degree_name !== '' && agent.degree_name !== null) {
      lineTwoArray.push(agent.degree_name);
    }
    if (agent.school_start !== '' && agent.school_start !== null) {
      if (agent.school_end !== '' && agent.school_end !== null) {
        lineTwoArray.push(`${agent.school_start} - ${agent.school_end}`);
      } else {
        lineTwoArray.push(agent.school_start);
      }
    } else if (agent.school_end !== '' && agent.school_end !== null) {
      lineTwoArray.push(agent.school_end);
    }

    const lineOne = lineOneArray.join(', ');
    const lineTwo = lineTwoArray.join(', ');
    return (
      <p className={css(styles.achievmentText)}>
        <span>{lineOne}</span>
        <br />
        <span>{lineTwo}</span>
      </p>
    );
  };

  const navigateFromPanel = (e) => {
    e.stopPropagation();
    e.preventDefault();
    router.push(`${formatAgentProfileURL(agent).url}`);
  };

  return (
    <div
      onClick={navigateFromPanel}
      className={css(styles.topPerformingAgentsCard)}
    >
      <div className={css(styles.header, isPrime && styles.primeHeader)}>
        <div className={css(styles.headerAgent)}>
          <Link
            prefetch={false}
            legacyBehavior
            href={formatAgentProfileURL(agent).path}
            as={formatAgentProfileURL(agent).url}
          >
            <a className={css(styles.link)}>
              <Image
                imageSrc={agent.photo_url}
                defaultSrc="/static/profile_default.webp"
                alt={`${agent.display_name} profile picture`}
                className={css(styles.picture)}
                containerClassName={css(
                  styles.profilePictureContainer,
                  isPrime && styles.primePicture
                )}
              />
            </a>
          </Link>
          <div className={css(styles.agentInfo)}>
            <p className={css(styles.brokerage)}>
              {capitalize(agent.display_brokerage)}
            </p>
            <Link
              prefetch={false}
              legacyBehavior
              href={formatAgentProfileURL(agent).path}
              as={formatAgentProfileURL(agent).url}
            >
              <a className={css(styles.link)}>
                <ProfileCompleteBadge
                  displayNameClassName={css(styles.name)}
                  agent={agent}
                />
              </a>
            </Link>
            {agent.review_total >= 1 && (
              <div className={css(styles.agentRatingContainer)}>
                <ReviewRatings
                  rating={agent.average_review_rating}
                  readOnly
                  starSize={styles.starSize}
                />
                <span className={css(styles.reviewText)}>
                  {agent.review_total > 1
                    ? `${agent.review_total} reviews`
                    : `${agent.review_total} review`}
                </span>
              </div>
            )}
            <div
              className={css(
                styles.actionButtons,
                !agent?.email && styles.actionButtonsNoEmail
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <MessageAgentButton
                agent={agent}
                className="--full-width"
                funnel="message_agent_city_2"
                skipLocation={skipLocation}
                noCompetingProposals={noCompetingProposals}
                messageHeader={messageHeader}
              >
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className={css(styles.envelope)}
                />
                {messageActionTextFunc
                  ? messageActionTextFunc(displayNameOrFirstName)
                  : `Message ${displayNameOrFirstName}`}
              </MessageAgentButton>
              <Link
                prefetch={false}
                legacyBehavior
                href={`${formatAgentProfileURL(agent).path}`}
                as={`${formatAgentProfileURL(agent).url}`}
              >
                <a className={css(styles.buttonLink)}>
                  <Button
                    text="View Profile"
                    buttonStyles={[
                      styles.button,
                      styles.viewProfileButton,
                      isPrime && styles.primeButton,
                    ]}
                  />
                </a>
              </Link>
            </div>
            {agent.response_time ||
            (agent.business_hours && agent.business_hours.length > 0) ? (
              <div className={css(styles.responseContainer)}>
                {agent.business_hours && agent.business_hours.length > 0 && (
                  <OpenAt
                    hours={agent.business_hours}
                    highlight={styles.responseHighlight}
                    timezone={agent.timezone}
                  />
                )}
                {agent.response_time && (
                  <RespondsIn
                    responseTime={agent.response_time}
                    highlight={styles.responseHighlight}
                  />
                )}
              </div>
            ) : null}
          </div>
          {inNetwork ? (
            <div className={css(styles.badges)}>
              {isPrime && (
                <PrimeBadge
                  agent={agent}
                  badgeStyles={[styles.primeBadge]}
                  iconStyles={[styles.primeIcon]}
                />
              )}
              <div className={css(styles.verification)}>
                {agent.certified_realtor && (
                  <VerifiedBadge
                    iconAlt="realtor logo"
                    iconSrc="/static/realtor_logo.webp"
                    text="Certified REALTOR©"
                    badgeStyles={styles.badgeStyle}
                  />
                )}
                {agent.certified_license && (
                  <VerifiedBadge
                    iconAlt="verified license icon"
                    iconSrc="/static/verified_license.webp"
                    text="Verified License"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className={css(styles.notInNetworkAbout)}>
              <p className={css(styles.bodySectionTitle)}>About</p>
              <div className={css(styles.aboutText, styles.about)}>
                <Dotdotdot clamp={4}>
                  {agent.bio
                    ? agent.bio
                    : renderAboutAgent({
                        agent,
                        city: location.city,
                        state: location.state,
                      })}
                </Dotdotdot>
                <Link
                  prefetch={false}
                  legacyBehavior
                  href={formatAgentProfileURL(agent).path}
                  as={formatAgentProfileURL(agent).url}
                >
                  <a>
                    <p className={css(styles.readMore)}>
                      <span>Read more</span>
                      <FontAwesomeIcon
                        icon={faCaretRight}
                        className={css(styles.caret)}
                      />
                    </p>
                  </a>
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className={css(styles.divider, isPrime && styles.primeDivider)} />
        <div className={css(styles.stats)}>
          <HeaderStat
            value={
              agent.display_years_experience < 1
                ? 1
                : Math.ceil(agent.display_years_experience)
            }
            label={
              agent.display_years_experience <= 1
                ? 'year of experience'
                : 'years of experience'
            }
            statContainerStyles={[styles.statContainerStyle]}
            valueStyles={[styles.statValueStyle]}
          />
          {!!agent.display_sales_last_12_months && (
            <HeaderStat
              value={
                agent.display_sales_last_12_months &&
                numeral(agent.display_sales_last_12_months).format(
                  typeof agent.display_sales_last_12_months === 'number'
                    ? '0,0'
                    : agent.display_sales_last_12_months.includes('$')
                    ? '$0,0'
                    : '0,0'
                )
              }
              label="sales in last 12 months"
              statContainerStyles={[styles.statContainerStyle]}
              valueStyles={[styles.statValueStyle]}
            />
          )}
          {!!agent.display_transactions_all_time && (
            <HeaderStat
              value={numeral(agent.display_transactions_all_time).format('0,0')}
              label="transactions all-time"
              statContainerStyles={[styles.statContainerStyle]}
              valueStyles={[styles.statValueStyle]}
            />
          )}
          <div className={css(styles.serves)}>
            {`Serves ${location.location}`}
          </div>
        </div>
      </div>
      {inNetwork && (
        <div className={css(styles.body)}>
          <div className={css(styles.bodySection)}>
            <p className={css(styles.bodySectionTitle)}>About</p>
            <div className={css(styles.aboutText, styles.about)}>
              <Dotdotdot clamp={4}>
                {agent.bio
                  ? agent.bio
                  : renderAboutAgent({
                      agent,
                      city: location.city,
                      state: location.state,
                    })}
              </Dotdotdot>
              <Link
                prefetch={false}
                legacyBehavior
                href={formatAgentProfileURL(agent).path}
                as={formatAgentProfileURL(agent).url}
              >
                <a>
                  <p className={css(styles.readMore)}>
                    <span>Read more</span>
                    <FontAwesomeIcon
                      icon={faCaretRight}
                      className={css(styles.caret)}
                    />
                  </p>
                </a>
              </Link>
            </div>
            {agent.testimonial && (
              <div>
                <div className={css(styles.testimonial)}>
                  <div className={css(styles.quotation)}>{'\u201c'}</div>
                  <div className={css(styles.aboutText)}>
                    <Dotdotdot clamp={2}>
                      <p className={css(styles.testimonialText)}>
                        {agent.testimonial.review}
                      </p>
                    </Dotdotdot>
                    <Link
                      prefetch={false}
                      legacyBehavior
                      href={formatAgentProfileURL(agent).path}
                      as={formatAgentProfileURL(agent).url}
                      anchor="#reviews"
                    >
                      <a>
                        <span className={css(styles.readMore)}>
                          <span>Read more</span>
                          <FontAwesomeIcon
                            icon={faCaretRight}
                            className={css(styles.caret)}
                          />
                        </span>
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          {achievementsSectionExists() && (
            <div className={css(styles.bodySection)}>
              <p className={css(styles.bodySectionTitle)}>Achievements</p>
              {(isPrime || isNonPrime) &&
                achievementsExist({ achievements: agent.achievements }) && (
                  <div className={css(styles.achievementBadgesContainer)}>
                    <Achievements
                      agent={agent}
                      city={
                        agent.display_city
                          ? agent.display_city.toLowerCase()
                          : null
                      }
                      state={
                        agent.display_state
                          ? agent.display_state?.toLowerCase()
                          : null
                      }
                      badgeStyles={[styles.achievementBadge]}
                      iconStyles={[styles.achievementBadgeIcon]}
                    />
                  </div>
                )}
              {agent.agent_awards !== 0 &&
                agent.agent_awards !== null &&
                agent.agent_awards.trim() !== '' && (
                  <div className={css(styles.achievementContainer)}>
                    <div className={css(styles.starContainer)}>
                      <FontAwesomeIcon
                        icon={faStar}
                        className={css(styles.achievementIcon, styles.star)}
                      />
                    </div>
                    <p className={css(styles.achievmentText)}>
                      <Dotdotdot tagName="span" clamp={2}>
                        {agent.agent_awards}
                      </Dotdotdot>
                    </p>
                  </div>
                )}
              {educationExists() && (
                <div className={css(styles.achievementContainer)}>
                  <FontAwesomeIcon
                    icon={faGraduationCap}
                    className={css(styles.achievementIcon)}
                  />
                  {renderEducation()}
                </div>
              )}
              {agent.designations !== 0 &&
                agent.designations !== null &&
                agent.designations.trim() !== '' && (
                  <div className={css(styles.achievementContainer)}>
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      className={css(styles.achievementIcon)}
                    />
                    <p className={css(styles.achievmentText)}>
                      {renderDesignations()}
                    </p>
                  </div>
                )}
            </div>
          )}
          <div
            className={css(
              styles.bodySection,
              styles.businessHighlightsSection
            )}
          >
            {(isPrime || isNonPrime) && agent.video_location ? (
              <>
                <p className={css(styles.bodySectionTitle)}>
                  {`${agent.display_name}'s Video`}
                </p>
                <div className={css(styles.videoContainer)}>
                  <VideoPlayer
                    url={agent.video_location}
                    width="100%"
                    height="249px"
                  />
                </div>
              </>
            ) : (
              (agent.specialties ||
                agent.highlight_one ||
                agent.highlight_two ||
                agent.highlight_three) && (
                <>
                  <p className={css(styles.bodySectionTitle)}>
                    Business Highlights
                  </p>
                  <div className={css(styles.businessHighlightsContainer)}>
                    {agent.specialties && (
                      <div className={css(styles.highlight)}>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className={css(styles.checkCircle)}
                        />
                        <Dotdotdot clamp={2}>{renderSpecialties()}</Dotdotdot>
                      </div>
                    )}
                    {agent.highlight_one && (
                      <div className={css(styles.highlight)}>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className={css(styles.checkCircle)}
                        />
                        <Dotdotdot clamp={2}>{agent.highlight_one}</Dotdotdot>
                      </div>
                    )}
                    {agent.highlight_two && (
                      <div className={css(styles.highlight)}>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className={css(styles.checkCircle)}
                        />
                        <Dotdotdot clamp={2}>{agent.highlight_two}</Dotdotdot>
                      </div>
                    )}
                    {agent.highlight_three && (
                      <div className={css(styles.highlight)}>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className={css(styles.checkCircle)}
                        />
                        <Dotdotdot clamp={2}>{agent.highlight_three}</Dotdotdot>
                      </div>
                    )}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  topPerformingAgentsCard: {
    borderRadius: 5,
    marginBottom: 60,
    boxShadow: '0px 0px 28px 0px rgba(30, 41, 59, 0.08)',
    position: 'relative',
    border: '1px solid rgba(233, 233, 233, 1)',
    cursor: 'pointer',
  },
  header: {
    borderRadius: 5,
    boxShadow: '0px 0px 28px 0px rgba(30, 41, 59, 0.08)',
    zIndex: 3,
  },
  primeHeader: {
    background: COLORS.PRIME_BLUE(),
    color: '#fff',
  },
  profilePictureContainer: {
    height: 153,
    width: 153,
    position: 'relative',
    borderRadius: '100%',
    overflow: 'hidden',
    marginRight: 32,

    '@media only screen and (max-width: 1023px)': {
      marginRight: 0,
      height: 120,
      width: 120,
      marginRight: 0,
      marginBottom: 12,
    },
  },
  primePicture: {
    border: '4px solid rgba(211, 161, 59, 1)',
  },
  headerAgent: {
    display: 'flex',
    padding: '30px 35px',

    '@media only screen and (max-width: 1023px)': {
      flexDirection: 'column',
      alignItems: 'center',
    },

    '@media only screen and (max-width: 474px)': {
      padding: '20px 25px',
    },
  },
  agentRatingContainer: {
    marginBottom: 8,

    '@media only screen and (max-width: 1023px)': {
      marginBottom: 24,
    },

    '@media only screen and (max-width: 767px)': {
      marginBottom: 20,
    },
  },
  starSize: {
    width: 16,
    height: 16,
  },
  reviewText: {
    fontSize: 12,
    fontWeight: 400,
    marginLeft: 8,
    opacity: 0.8,
  },
  brokerage: {
    fontWeight: 400,
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 8,
  },
  agentInfo: {
    '@media only screen and (max-width: 1023px)': {
      width: '100%',
      textAlign: 'center',
    },
  },
  name: {
    fontWeight: 600,
    fontSize: 24,
    marginBottom: 10,
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    '@media only screen and (max-width: 1023px)': {
      justifyContent: 'center',
    },

    '@media only screen and (max-width: 767px)': {
      justifyContent: 'space-between',
      flexDirection: 'column',
    },
  },
  messageAgentButton: {
    background: COLORS.PRIMARY_ACTION(),
  },
  viewProfileButton: {
    background: 'transparent',
    border: '1px solid rgba(148, 163, 184, 1)',
    color: COLORS.BLACK(),
    marginLeft: 10,
    marginBottom: 16,
    marginTop: 20,

    '@media only screen and (max-width: 1023px)': {
      width: '100%',
    },

    '@media only screen and (max-width: 767px)': {
      height: 48,
      fontSize: 14,
      marginLeft: 0,
      marginBottom: 16,
      marginTop: 0,
    },
  },
  primeButton: {
    color: '#fff',
  },
  button: {
    width: 198,
    height: 48,
    fontWeight: 600,
    fontSize: 16,
    borderRadius: 5,

    '@media only screen and (max-width: 1030px)': {
      width: 170,
    },
    '@media only screen and (max-width: 1023px)': {
      width: 198,
      fontSize: 14,
    },
    '@media only screen and (max-width: 767px)': {
      width: '100%',
      height: 48,
      fontSize: 14,
    },

    '@media only screen and (max-width: 374px)': {
      width: '100%',
      marginBottom: 10,
    },
  },
  envelope: {
    marginRight: 7,
  },
  badges: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  responseContainer: {
    display: 'flex',
    alignItems: 'center',

    '@media only screen and (max-width: 1023px)': {
      flexDirection: 'column',
    },
  },
  responseHighlight: {
    color: 'rgb(4, 162, 130)',
  },
  stats: {
    gridTemplateColumns: '1fr 1fr 1fr .8fr',
    display: 'grid',
    padding: '20px 35px',
    zIndex: 3,
    position: 'relative',
    alignItems: 'center',
    boxShadow: '0px 23px 23px -23px rgba(30, 41, 59, 0.08)',
    columnGap: 20,

    '@media only screen and (max-width: 1023px)': {
      gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    },
  },
  serves: {
    fontWeight: 600,
    fontSize: 14,
    gridColumnEnd: '-1',
    textAlign: 'end',

    '@media only screen and (max-width: 1023px)': {
      textAlign: 'center',
    },
  },
  divider: {
    opacity: 0.1,
    borderTop: ' 1px solid #47505F',
  },
  primeDivider: {
    opacity: 1,
  },
  badgeStyle: {
    marginBottom: 9,
  },
  picture: {
    borderRadius: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
  },
  body: {
    background: '#fff',
    padding: '30px 35px',
    zIndex: 1,
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    display: 'grid',
    columnGap: 30,

    '@media only screen and (max-width: 1023px)': {
      gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
      rowGap: 24,
      padding: '20px 25px',
    },
  },
  verification: {
    marginTop: 'auto',
  },
  bodySectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 16,

    '@media only screen and (max-width: 1023px)': {
      marginBottom: 12,
    },
  },
  aboutText: {
    fontSize: 14,
    fontWeight: 400,
    wordWrap: 'break-word',
  },
  bodySection: {
    width: '100%',
  },
  readMore: {
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    marginTop: 4,
  },
  caret: {
    marginLeft: 8,
    fontSize: 12,
  },
  about: {
    marginBottom: 30,

    '@media only screen and (max-width: 1023px)': {
      marginBottom: 18,
    },
  },
  testimonial: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  quotation: {
    fontSize: 50,
    lineHeight: '45px',
    fontFamily: 'Georgia',
    marginRight: 8,
  },
  achievementBadgesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: 22,

    '@media only screen and (max-width: 1023px)': {
      marginBottom: 18,
    },
  },
  achievmentText: {
    fontSize: 14,
    fontWeight: 500,
    width: 'calc(100% - 32px)',
  },
  achievementIcon: {
    width: 16,
    height: 16,
    color: 'rgba(0, 107, 184, 1)',
    marginRight: 12,
    marginTop: 3,
    background: 'transparent',
  },
  starContainer: {
    background: 'rgba(0, 107, 184, 1)',
    height: 16,
    width: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '100%',
    boxSizing: 'border-box',
    marginRight: 12,
    marginTop: 3,
  },
  star: {
    width: 10,
    height: 10,
    color: '#fff',
    marginRight: 0,
    marginTop: 0,
  },
  achievementContainer: {
    display: 'flex',
    marginBottom: 12,
  },
  checkCircle: {
    width: 16,
    height: 16,
    color: 'rgba(0, 107, 184, 1)',
    marginRight: 9,
    marginTop: 3,
    background: 'transparent',
  },
  highlight: {
    display: 'flex',
    marginBottom: 10,
    fontSize: 14,
  },
  notInNetworkAbout: {
    width: '30%',
    marginLeft: 'auto',

    '@media only screen and (max-width: 1023px)': {
      width: '100%',
    },
  },
  achievementBadges: {
    '@media only screen and (max-width: 1200px)': {
      width: 'unset',
    },
  },
  statContainerStyle: {
    '@media only screen and (max-width: 1023px)': {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'center',
      marginBottom: 12,
    },
  },
  statValueStyle: {
    '@media only screen and (max-width: 1023px)': {
      fontSize: 20,
      marginRight: 2,
    },
    '@media only screen and (max-width: 767px)': {
      marginRight: 5,
    },
  },
  primeBadge: {
    '@media only screen and (max-width: 1023px)': {
      position: 'absolute',
      right: 20,
      top: 20,
    },

    '@media only screen and (max-width: 767px)': {
      padding: '4px 12px',
      fontSize: 9,
    },

    '@media only screen and (max-width: 374px)': {
      right: 10,
      top: 10,
      padding: '3px 10px',
      fontSize: 8,
    },
  },
  primeIcon: {
    width: 26,
    height: 26,

    '@media only screen and (max-width: 767px)': {
      width: 18,
      height: 18,
      marginRight: 5,
    },

    '@media only screen and (max-width: 374px)': {
      width: 15,
      height: 15,
      marginRight: 3,
    },
  },
  buttonLink: {
    width: '100%',
  },
  link: {
    cursor: 'pointer',
  },
  achievementBadge: {
    fontSize: 14,
    fontWeight: 600,
    padding: '4px 13px',
    borderRadius: 40,
  },
  achievementBadgeIcon: {
    width: 16,
    height: 'unset',
  },
  testimonialText: {
    wordWrap: 'break-word',
    width: '90%',
  },
});
export default TopPerformingAgentsCard;
